import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import Guestheader from './headers/guestheader';
import AuthenticatedNavbar from './headers/AuthenticatedNavbar';
import PostList from './PostList';
import Loading from './icon/loading';
import { getApiBaseUrl } from '../config/api';
import { normalizeHashtag } from '../utils/mentions';
import './HashtagPage.css';

const PAGE_SIZE = 20;
const API_URL = getApiBaseUrl();

export default function HashtagPage({ user, onLogout }) {
  const { tag: routeTag } = useParams();
  const location = useLocation();
  const tag = normalizeHashtag(routeTag || location.hash || '');

  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(Boolean(tag));
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const fetchPosts = useCallback(
    async ({ reset = true, offset = 0 } = {}) => {
      if (!tag || !API_URL) {
        setPosts([]);
        setTotal(0);
        setHasMore(false);
        setLoading(false);
        return;
      }

      if (reset) {
        setLoading(true);
        setError('');
      } else {
        setLoadingMore(true);
      }

      const params = new URLSearchParams({
        hashtag: tag,
        limit: String(PAGE_SIZE),
        offset: String(reset ? 0 : offset),
      });
      if (user?.id) {
        params.set('viewer_id', String(user.id));
      }

      try {
        const res = await fetch(`${API_URL}/get_posts.php?${params.toString()}`);
        const data = await res.json();
        if (!data?.success) {
          throw new Error(data?.message || 'Could not load tagged posts.');
        }
        const nextPosts = Array.isArray(data.posts) ? data.posts : [];
        setPosts((prev) => {
          if (reset) return nextPosts;
          const seen = new Set(prev.map((p) => p.id));
          return [...prev, ...nextPosts.filter((p) => !seen.has(p.id))];
        });
        setTotal(Number(data.total) || nextPosts.length);
        setHasMore(Boolean(data.has_more));
      } catch (err) {
        setError(err.message || 'Could not load tagged posts.');
        if (reset) {
          setPosts([]);
          setTotal(0);
          setHasMore(false);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [tag, user?.id]
  );

  useEffect(() => {
    fetchPosts({ reset: true });
  }, [fetchPosts]);

  useEffect(() => {
    const previous = document.title;
    document.title = tag
      ? `#${tag} · ReallyLesson COOL`
      : 'Hashtags · ReallyLesson COOL';
    return () => {
      document.title = previous;
    };
  }, [tag]);

  const handleRefresh = async () => {
    await fetchPosts({ reset: true });
  };

  const homeTo = user ? '/home' : '/';

  return (
    <div className="hashtag-page">
      {user ? (
        <AuthenticatedNavbar user={user} onLogout={onLogout} />
      ) : (
        <div className="hashtag-page-guest-nav">
          <Guestheader />
        </div>
      )}

      <main className="hashtag-page-main">
        <Link to={homeTo} className="hashtag-back">
          ← Back to discussions
        </Link>

        {!tag ? (
          <div className="hashtag-empty">
            <h1 className="hashtag-title">Hashtag</h1>
            <p>This tag is missing or invalid. Try a link like #CoolJobs from a post.</p>
          </div>
        ) : (
          <>
            <header className="hashtag-hero">
              <p className="hashtag-kicker">Hashtag</p>
              <h1 className="hashtag-title">#{tag}</h1>
              <p className="hashtag-count">
                {loading
                  ? 'Loading posts…'
                  : `${total} post${total === 1 ? '' : 's'}`}
              </p>
            </header>

            {loading ? (
              <div className="hashtag-loading">
                <Loading />
              </div>
            ) : error ? (
              <div className="hashtag-empty">
                <p>{error}</p>
                <button type="button" className="hashtag-more-btn" onClick={handleRefresh}>
                  Try again
                </button>
              </div>
            ) : posts.length === 0 ? (
              <div className="hashtag-empty">
                <p>No posts use #{tag} yet.</p>
                <Link to={homeTo} className="hashtag-empty-link">
                  Browse discussions
                </Link>
              </div>
            ) : (
              <>
                <PostList
                  posts={posts}
                  user={user}
                  onReply={() => {}}
                  onRefresh={handleRefresh}
                  preserveOrder
                  hidePagination
                  compact
                />
                {hasMore && (
                  <div className="hashtag-more">
                    <button
                      type="button"
                      className="hashtag-more-btn"
                      disabled={loadingMore}
                      onClick={() => fetchPosts({ reset: false, offset: posts.length })}
                    >
                      {loadingMore ? 'Loading…' : 'Load more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
